import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import StatusColumn from './StatusColumn';
import CustomerCard from './CustomerCard'; // Importar CustomerCard
import ChatView from './ChatView'; // Importar o novo componente de chat
import ChatSidebar from './ChatSidebar'; // Importa o novo componente
import RegisterSaleModal from './RegisterSaleModal';
import StockStatus from './StockStatus'; // Importa o novo componente de estoque
import OrderCard from './OrderCard';
import styles from './Dashboard.module.css'; // Importa os estilos do Dashboard
import orderFilterStyles from './OrderFilters.module.css';
import { getOrders, getCustomers, getCustomerMessages, updateOrderStatus, getProducts, confirmPayment } from '../services/apiServices';
import ManageMotoboysModal from './ManageMotoboysModal'; // Importa o modal de motoboys
import SalesSummaryModal from './SalesSummaryModal'; // Importa o novo modal de resumo de vendas
import EditStockModal from './EditStockModal'; // Importa o modal de edição de estoque
import DateFilter from './DateFilter'; // Importa o filtro de data
import MobileNav from './MobileNav'; // Importa a nova navegação móvel
import { FaSearch } from 'react-icons/fa'; // Importa o ícone de busca
import mobileStyles from './RegisterSaleModal.module.css'; // Importa estilos para o botão de voltar
import logo from '../assets/images/logo.png';


function Dashboard() {
  const { socket, isSoundEnabled, notificationSoundRef } = useOutletContext();
  const { user } = useAuth(); // Pega o usuário logado do contexto
  const [orders, setOrders] = useState(() => {
    const savedOrders = localStorage.getItem('orders');
    const initialOrders = savedOrders ? JSON.parse(savedOrders) : {};
    // CORREÇÃO: Garante que todas as chaves existam como arrays, mesmo que não venham do localStorage.
    return {
      delivery: initialOrders.delivery || [],
      pickup: initialOrders.pickup || [],
      completed: initialOrders.completed || [],
    };
  });
  const [isMotoboyModalOpen, setIsMotoboyModalOpen] = useState(false); // Estado para o modal de gerenciar motoboys

  const [customers, setCustomers] = useState([]); // Novo estado para clientes
  const [products, setProducts] = useState({}); // Novo estado para o estoque
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // MODIFICAÇÃO: Lê os chats abertos do localStorage na inicialização
  const [openChats, setOpenChats] = useState(() => {
    const saved = localStorage.getItem('openChats');
    return saved ? JSON.parse(saved) : [];
  });
  // Novo estado para controlar as notificações em tempo real
  const [notifications, setNotifications] = useState(() => {
    const saved = localStorage.getItem('notifications');
    return saved ? JSON.parse(saved) : {};
  });
  // Lê os timestamps do localStorage na inicialização
  const [lastReadTimestamps, setLastReadTimestamps] = useState(() => {
    const saved = localStorage.getItem('lastReadTimestamps');
    return saved ? JSON.parse(saved) : {};
  });
  // Estado para o filtro de data única, inicializado como vazio.
  const [selectedDate, setSelectedDate] = useState('');
  // Novos estados para os filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [showWithSideDishes, setShowWithSideDishes] = useState(false);
  const [showOnlyPix, setShowOnlyPix] = useState(false);

  const [isSaleModalOpen, setIsSaleModalOpen] = useState(false); // Estado para controlar o modal
  const [saleModalMode, setSaleModalMode] = useState('sale'); // 'sale' ou 'push_cart'
  const [saleModalInitialData, setSaleModalInitialData] = useState(null); // Dados do cliente para o modal
  const [isSalesSummaryModalOpen, setIsSalesSummaryModalOpen] = useState(false); // Estado para o novo modal de vendas
  const [isEditStockModalOpen, setIsEditStockModalOpen] = useState(false); // Estado para o modal de estoque

  // Novo estado para controlar a visualização em telas móveis
  const [mobileView, setMobileView] = useState('pedidos');

  const [activeChatId, setActiveChatId] = useState(null); // ID do chat atualmente visível
  const activeChatIdRef = useRef(activeChatId); // Ref para evitar stale closure no socket
  activeChatIdRef.current = activeChatId;

  const isSoundEnabledRef = useRef(isSoundEnabled);
  const [isChatMenuExpanded, setIsChatMenuExpanded] = useState(false);
  isSoundEnabledRef.current = isSoundEnabled;

  const [chatMessages, setChatMessages] = useState([]); 
  const [isChatLoading, setIsChatLoading] = useState(false);
  // EFEITO 1: Responsável por buscar os dados iniciais e conectar o socket (roda apenas uma vez)
  useEffect(() => {
    // Função para buscar dados com base nos filtros atuais
    const fetchData = async () => {
      setLoading(true);
      try {
        // MODIFICAÇÃO: Passa a data selecionada para a API.
        // Se nenhuma data for selecionada, a API pode ter um comportamento padrão (ex: buscar hoje).
        const ordersData = await getOrders({ date: selectedDate });
        console.log('[Dashboard] Dados de pedidos carregados do backend:', ordersData);
        setOrders(ordersData);

        // Clientes e produtos ainda podem ser carregados, mas se a lista de clientes
        // crescer muito, ela também precisará de otimização.
        const customersData = await getCustomers();
        setCustomers(customersData);
        const productsData = await getProducts();
        setProducts(productsData);

      } catch (e) {
        console.error("Erro ao buscar dados:", e);
        setError("Falha ao carregar os dados. Verifique se o servidor backend está rodando.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();

    const playNotificationSound = () => {
      if (isSoundEnabledRef.current) {
        notificationSoundRef.current.play().catch(error => {
          console.log("A reprodução de som será ativada após a primeira interação do usuário.", error);
        });
      }
    };

    const handleNewMessage = (data) => {
      console.log('[Socket.IO] Nova mensagem recebida:', data);
    
      // CORREÇÃO: Simplifica a lógica para sempre adicionar a nova mensagem se ela existir e pertencer ao chat ativo.
      // Isso evita a re-busca desnecessária de todo o histórico.
      if (data && data.customerId === activeChatIdRef.current && data.message) {
        setChatMessages(currentMessages => {
          // --- PREVENÇÃO DE DUPLICIDADE ---
          // Verifica se a mensagem já existe no estado (comparando texto e timestamp)
          // Isso evita que a mensagem seja adicionada duas vezes (uma pelo socket, outra pela API)
          const messageExists = currentMessages.some(msg => {
            if (msg.timestamp !== data.message.timestamp) return false;
            
            // Se timestamps são iguais, verifica conteúdo
            // Para texto:
            if (msg.parts?.[0]?.text && data.message.parts?.[0]?.text) {
              return msg.parts[0].text === data.message.parts[0].text;
            }
            // Para documentos/imagens (se timestamps batem, assumimos duplicado para evitar repetição visual)
            if (msg.document && data.message.document) {
              // Se tiver ID (do WhatsApp), usa para diferenciar. Se não, assume duplicado pelo timestamp.
              return msg.document.id && data.message.document.id ? msg.document.id === data.message.document.id : true;
            }
            if (msg.image && data.message.image) {
              return msg.image.id && data.message.image.id ? msg.image.id === data.message.image.id : true;
            }
            
            return false;
          });

          if (messageExists) return currentMessages;
          // Se não existe, adiciona a nova mensagem ao final do array.
          return [...currentMessages, data.message];
        });
      } else if (data && data.customerId) {
        // Se a mensagem for para um chat não ativo, toca o som e mostra a notificação
        playNotificationSound();
        setNotifications(prev => {
          const newNotifications = { ...prev, [data.customerId]: true };
          localStorage.setItem('notifications', JSON.stringify(newNotifications));
          return newNotifications;
        });
      }
    };

    const handleNewOrder = (order) => {
      console.log('[Socket.IO] Novo pedido recebido:', order);
      playNotificationSound();
      // Adiciona o novo pedido à lista correta
      setOrders(prevOrders => {
        const newOrdersState = {};
        
        // 1. Remove o pedido se ele já existir em alguma lista (evita duplicação e cria uma nova referência de memória para o React)
        for (const key in prevOrders) {
          newOrdersState[key] = (prevOrders[key] || []).filter(o => o.id !== order.id);
        }

        // 2. Adiciona o pedido na lista correta
        if (order.status === 'completed' || order.status === 'canceled') {
          if (!newOrdersState.completed) newOrdersState.completed = [];
          newOrdersState.completed.unshift(order);
        } else {
          const targetList = order.deliveryType || 'pickup';
          if (!newOrdersState[targetList]) newOrdersState[targetList] = [];
          newOrdersState[targetList].push(order);
        }
        return newOrdersState;
      });
      // --- CORREÇÃO: Remove o cliente da coluna "Em Atendimento" ---
      // Filtra a lista de clientes, removendo aquele que acabou de fazer o pedido.
      setCustomers(prevCustomers => prevCustomers.filter(c => c.id !== order.customerId));
    };

    const handleOrderUpdated = (updatedOrder) => {
      console.log('[Socket.IO] Pedido atualizado recebido:', updatedOrder);
      // --- CORREÇÃO: Lógica de atualização para mover o pedido entre colunas ---
      setOrders(prevOrders => {
        const newOrdersState = {};
        
        // 1. Remove o pedido de todas as listas dinamicamente.
        for (const key in prevOrders) {
          newOrdersState[key] = (prevOrders[key] || []).filter(o => o.id !== updatedOrder.id);
        }

        // 2. Adiciona o pedido atualizado na lista correta.
        if (updatedOrder.status === 'completed' || updatedOrder.status === 'canceled') {
          if (!newOrdersState.completed) newOrdersState.completed = [];
          newOrdersState.completed.unshift(updatedOrder);
        } else {
          const targetKey = updatedOrder.deliveryType || 'pickup';
          if (!newOrdersState[targetKey]) newOrdersState[targetKey] = [];
          newOrdersState[targetKey].push(updatedOrder);
        }
        return newOrdersState;
      });
    };

    // NOVO HANDLER: Ouve por atualizações de um cliente específico e atualiza o estado localmente.
    const handleCustomerUpdated = (updatedCustomer) => {
      console.log('[Socket.IO] Cliente atualizado recebido:', updatedCustomer.id);
      setCustomers(prevCustomers => {
        const index = prevCustomers.findIndex(c => c.id === updatedCustomer.id);
        // Se o cliente já existe na lista, atualiza ele
        if (index !== -1) {
          const newCustomers = [...prevCustomers];
          newCustomers[index] = updatedCustomer;
          return newCustomers;
        }
        // Se for um cliente novo (primeira mensagem), adiciona ele à lista
        return [...prevCustomers, updatedCustomer];
      });

      // --- CORREÇÃO: Atualiza o chat ativo se o cliente atualizado for o mesmo ---
      // Isso garante que mensagens que não disparam 'newMessage' (como documentos) apareçam imediatamente.
      if (updatedCustomer.id === activeChatIdRef.current && updatedCustomer.geminiHistory) {
        const sortedHistory = [...updatedCustomer.geminiHistory].sort((a, b) => {
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return timeA - timeB;
        });
        setChatMessages(sortedHistory);
      }
    };

    const handleStockUpdated = () => {
      console.log('[Socket.IO] Evento stockUpdated recebido. Rebuscando produtos...');
      getProducts()
        .then(setProducts)
        .catch(e => console.error("Erro ao buscar produtos após atualização de estoque:", e));
    };

    // Adiciona uma verificação para garantir que o socket não é nulo
    if (!socket) {
      console.warn("[Dashboard] Socket ainda não está pronto. Aguardando conexão...");
      return; // Sai do useEffect se o socket não estiver disponível
    }

    // Registra os listeners
    socket.on('newMessage', handleNewMessage);
    socket.on('newOrder', handleNewOrder);
    socket.on('customerUpdated', handleCustomerUpdated); // Adiciona o novo listener
    socket.on('orderUpdated', handleOrderUpdated);
    socket.on('stockUpdated', handleStockUpdated);

    // Função de limpeza para remover os listeners
    return () => { // Adicionado o socket como dependência para garantir que os listeners sejam removidos corretamente
      socket.off('newMessage', handleNewMessage);
      socket.off('newOrder', handleNewOrder);
      socket.off('customerUpdated', handleCustomerUpdated); // Remove o novo listener
      socket.off('orderUpdated', handleOrderUpdated);
      socket.off('stockUpdated', handleStockUpdated);
    };
  }, [socket, selectedDate, notificationSoundRef]); // MODIFICAÇÃO: Adiciona selectedDate e notificationSoundRef como dependência

  // O useEffect acima agora depende de `selectedDate`.
  // Isso significa que toda vez que o usuário mudar a data no DateFilter,
  // uma nova busca será feita na API, trazendo apenas os pedidos daquele dia.

  // Efeito para buscar as mensagens do chat quando o chat ativo muda
  useEffect(() => {
    // Se nenhum chat estiver ativo, simplesmente limpa as mensagens e para.
    if (!activeChatId) {
      setChatMessages([]);
      return;
    }

    // Lógica robusta para carregar o histórico:
    // 1. Define o estado de loading para true.
    // 2. Chama a API para buscar o histórico completo e ORDENADO.
    // 3. Atualiza o estado `chatMessages` com os dados recebidos.
    // As novas mensagens serão adicionadas em tempo real pelo listener do socket.
    console.log(`[Chat] Abrindo chat para ${activeChatId}. Buscando histórico completo na API...`);
    setIsChatLoading(true);
    getCustomerMessages(activeChatId)
      .then(messages => {
        setChatMessages(messages); // A API agora retorna o histórico ordenado.
      })
      .catch(error => console.error("Erro ao buscar mensagens do chat via API:", error))
      .finally(() => setIsChatLoading(false));

  }, [activeChatId]);

  // Mapeia o status do USUÁRIO para a coluna "Em Atendimento"
  const getColumnForCustomerStatus = (customerStatus) => {
    // Lista de status que NÃO devem aparecer em "Em Atendimento"
    const inactiveStatus = [
      'idle', 
      'awaiting_admin_mode_choice', 
      'awaiting_delivery', // Adicionado: Cliente já pediu e está aguardando entrega
      'awaiting_pickup',   // Adicionado: Cliente já pediu e está aguardando retirada
      undefined
    ];

    // Se o status do cliente NÃO estiver na lista de inativos, ele está em atendimento.
    if (!inactiveStatus.includes(customerStatus)) {
      return 'Em Atendimento';
    }
    return null; // Não pertence a esta coluna
  };

  // Mapeia o status do PEDIDO para as colunas do painel (Retirada, Entrega, Finalizado)
  const getColumnForOrderStatus = (orderStatus, deliveryType) => {
    // Status finais têm prioridade máxima
    if (['completed', 'canceled', 'out_for_delivery'].includes(orderStatus)) {
      return 'Finalizado';
    }
    
    // Se não for um status final, separamos pelo tipo de entrega
    // Assim, nenhum pedido some do painel acidentalmente se ganhar um status novo (como 'seen' ou 'prepared')
    if (deliveryType === 'delivery') {
      return 'Entrega';
    }
    
    // 'pickup', 'counter' ou fallback vão para a coluna de Retirada
    return 'Retirada';
  };

  // Memoiza a lista base de todos os pedidos para evitar recálculos desnecessários.
  const baseAllOrders = useMemo(() => 
    [...(orders.delivery || []), ...(orders.pickup || []), ...(orders.completed || [])]
  , [orders]);

  // REMOÇÃO: A filtragem de data agora é feita no backend.
  // O código abaixo que filtrava por data no cliente foi removido.
  // A variável `baseAllOrders` já contém os pedidos filtrados pela data que veio da API.
  let allOrders = baseAllOrders;

  // if (selectedDate) {
  //   allOrders = allOrders.filter(order => { ... });
  // }

  // Aplica o filtro de busca por nome ou número do pedido
  if (searchTerm) {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();
    allOrders = allOrders.filter(order =>
      (order.customerName || '').toLowerCase().includes(lowerCaseSearchTerm) ||
      String(order.orderNumber).includes(lowerCaseSearchTerm) ||
      (order.customerId || '').includes(lowerCaseSearchTerm) // Adiciona filtro por telefone (customerId)
    );
  }

  // Aplica o filtro para mostrar apenas pedidos com acompanhamentos
  if (showWithSideDishes) {
    allOrders = allOrders.filter(order =>
      order.cart.some(item =>
        products[item.product]?.category === 'acompanhamentos'
      )
    );
  }

  // Aplica o filtro para mostrar apenas pedidos com pagamento em Pix
  if (showOnlyPix) {
    allOrders = allOrders.filter(order => 
      // Verifica se o método de pagamento contém a palavra "Pix" (case-insensitive)
      order.paymentMethod && order.paymentMethod.toLowerCase().includes('pix')
    );
  }

  // Define as colunas visíveis com base no role do usuário
  const visibleColumns = useMemo(() => {
    if (user?.role === 'admin') {
      return ['Em Atendimento', 'Retirada', 'Entrega', 'Finalizado'];
    }
    if (user?.role === 'operator') {
      return ['Retirada', 'Finalizado']; // Vendedor vê Retirada e os Finalizados do dia
    }
    return []; // Nenhum role, não vê nada
  }, [user]);

  // MODIFICAÇÃO: Simplificado para sempre tentar renderizar as colunas visíveis.
  // O CSS será responsável por esconder as colunas desnecessárias em telas móveis.
  const columnsToRender = visibleColumns;

  // Agora, distribui os pedidos (já filtrados por data, se aplicável) para as colunas corretas.
  const columns = {
    'Em Atendimento': customers.filter(c => {
      // Condição para não mostrar clientes de vendas manuais (ID começa com 'manual_')
      const isManualCustomer = c.id.startsWith('manual_');
      return !isManualCustomer && getColumnForCustomerStatus(c.status) === 'Em Atendimento';
    }),
    // Ordena os pedidos pendentes do mais antigo para o mais novo
    'Retirada': allOrders.filter(o => getColumnForOrderStatus(o.status, o.deliveryType) === 'Retirada').sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),
    'Entrega': allOrders.filter(o => getColumnForOrderStatus(o.status, o.deliveryType) === 'Entrega').sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp)),    
    // Ordena os pedidos finalizados do mais novo para o mais antigo
    'Finalizado': allOrders.filter(o => getColumnForOrderStatus(o.status, o.deliveryType) === 'Finalizado').sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)),
  };

  // Função para abrir um chat e torná-lo ativo
  const handleOpenChat = (customerToOpen) => {
    setOpenChats(prevChats => {
      // Atualiza o timestamp de leitura e salva no localStorage
      setLastReadTimestamps(prev => {
        const newTimestamps = { ...prev, [customerToOpen.id]: customerToOpen.lastInteractionTimestamp };
        localStorage.setItem('lastReadTimestamps', JSON.stringify(newTimestamps));
        return newTimestamps;
      });
      
      // Remove a notificação para este cliente e salva no localStorage
      setNotifications(prev => {
        const newNotifications = { ...prev, [customerToOpen.id]: false };
        localStorage.setItem('notifications', JSON.stringify(newNotifications));
        return newNotifications;
      });

      // Adiciona o chat à lista apenas se ele não estiver lá
      if (!prevChats.some(chat => chat.id === customerToOpen.id)) {
        return [...prevChats, customerToOpen];
        
      }
      return prevChats;
    });
    // Define o chat clicado como o ativo
    setActiveChatId(customerToOpen.id);
  };

  // Efeito para salvar os chats abertos no localStorage sempre que eles mudarem
  useEffect(() => {
    localStorage.setItem('openChats', JSON.stringify(openChats));
  }, [openChats]);


  // Função para fechar um chat específico da barra lateral
  const handleCloseChat = (customerIdToClose) => {
    setOpenChats(prevChats => prevChats.filter(chat => chat.id !== customerIdToClose));
    // Se o chat fechado era o ativo, limpa a seleção para não mostrar nenhuma janela
    if (activeChatId === customerIdToClose) {
      setActiveChatId(null);
    }
  };

  // Encontra o objeto completo do cliente cujo chat está ativo
  const activeCustomer = openChats.find(chat => chat.id === activeChatId);

  // Encontra o pedido mais recente para o cliente ativo, se houver
  const activeOrder = activeCustomer
    ? [...(orders.delivery || []), ...(orders.pickup || [])]
        .filter(o => o.customerId === activeCustomer.id)
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0] // Pega o mais recente
    : null;

  const handleSelectActiveChat = (customerId) => {
    // Se o menu de chat estiver expandido, clicar em um chat deve abrir a janela
    // e recolher o menu para não poluir a tela.
    if (isChatMenuExpanded) {
      setIsChatMenuExpanded(false);
    }

    // Ao selecionar um chat, atualiza a contagem para "ler" as novas mensagens
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      setLastReadTimestamps(prev => {
        const newTimestamps = { ...prev, [customerId]: customer.lastInteractionTimestamp };
        localStorage.setItem('lastReadTimestamps', JSON.stringify(newTimestamps));
        return newTimestamps;
      });

      // Remove a notificação para este cliente e salva no localStorage
      setNotifications(prev => {
        const newNotifications = { ...prev, [customerId]: false };
        localStorage.setItem('notifications', JSON.stringify(newNotifications));
        return newNotifications;
      });
    }
    setActiveChatId(customerId);
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    console.log(`[DASHBOARD] handleUpdateOrderStatus - Enviando para API: orderId=${orderId}, status=${status}`);
    const originalOrders = orders;
    try {
      // Atualização otimista
      setOrders(prevOrders => {
        const allOrdersArray = Object.values(prevOrders).flat();
        const orderToUpdate = allOrdersArray.find(o => o.id === orderId);

        if (!orderToUpdate) return prevOrders;

        const updatedOrder = { ...orderToUpdate, status };
        const newOrdersState = {};

        // Filtra o pedido de todas as listas existentes
        for (const key in prevOrders) {
          newOrdersState[key] = (prevOrders[key] || []).filter(o => o.id !== orderId);
        }

        if (status === 'completed' || status === 'canceled') {
          if (!newOrdersState.completed) newOrdersState.completed = [];
          newOrdersState.completed.push(updatedOrder);
        } else {
          const targetKey = updatedOrder.deliveryType || 'pickup';
          if (!newOrdersState[targetKey]) newOrdersState[targetKey] = [];
          newOrdersState[targetKey].push(updatedOrder);
        }
        return newOrdersState;
      });

      await updateOrderStatus(orderId, status);
      // A atualização visual final virá pelo evento de socket 'orderUpdated'
    } catch (error) {
      console.error(`Erro ao atualizar pedido ${orderId} para ${status}:`, error);
      alert('Falha ao atualizar o status do pedido. Revertendo a alteração.');
      setOrders(originalOrders); // Reverte em caso de erro
    }
  };

  const handleConfirmPayment = async (orderId) => {
    const originalOrders = orders;
    try {
      // Atualização otimista
      setOrders(prevOrders => {
        const newState = {};
        for (const key in prevOrders) {
          newState[key] = (prevOrders[key] || []).map(order =>
            order.id === orderId ? { ...order, paymentConfirmed: true } : order
          );
        }
        return newState;
      });

      await confirmPayment(orderId);
      // A atualização visual final virá pelo evento de socket 'orderUpdated'
    } catch (error) {
      console.error(`Erro ao confirmar pagamento para o pedido ${orderId}:`, error);
      alert('Falha ao confirmar o pagamento. Revertendo a alteração.');
      setOrders(originalOrders); // Reverte em caso de erro
    }
  };

  // Função para abrir o modal de venda no modo de edição
  const handleEditOrder = (orderToEdit) => {
    setSaleModalMode('edit');
    setSaleModalInitialData(orderToEdit);
    setIsSaleModalOpen(true);
  };

  // Função para o ChatView chamar quando o admin envia uma mensagem
  const handleAdminMessageSent = (customerId, messageText) => {
    // Cria um objeto de mensagem no formato que o ChatView espera
    const newMessageObject = {
      role: 'model', // 'model' é usado para o admin/bot
      text: { body: messageText },
      timestamp: new Date().toISOString() // Padroniza para ISO string
    };

    // Adiciona a mensagem ao estado local do chat em tempo real
    if (customerId === activeChatId) {
      setChatMessages(currentMessages => [...currentMessages, newMessageObject]);
    }
  };

  // Função para abrir o modal de "Montar Carrinho" a partir do Chat
  const handlePushCart = (customer) => {
    setSaleModalMode('push_cart');
    setSaleModalInitialData(customer);
    setIsSaleModalOpen(true);
  };

  const unreadCount = useMemo(() => {
    return openChats.filter(chat => {
      const lastRead = lastReadTimestamps[chat.id];
      const customer = customers.find(c => c.id === chat.id);
      if (!customer) return false;
      // A condição `chat.id !== activeChatId` garante que a notificação não apareça para o chat que já está aberto e ativo.
      return (lastRead ? new Date(customer.lastInteractionTimestamp) > new Date(lastRead) : true) && chat.id !== activeChatId;
    }).length;
  }, [openChats, lastReadTimestamps, customers, activeChatId]);

  // Calcula os dados de vendas do dia (contagem e lista de produtos)
  const todaysSalesData = useMemo(() => {
    const todayString = new Date().toDateString();
    const todaysOrders = allOrders.filter(order => 
      new Date(order.timestamp).toDateString() === todayString
    );

    // Agrupa os itens por status (pendente vs. finalizado)
    const pendingItems = {};
    const completedItems = {};

    todaysOrders.forEach(order => {
      const isCompleted = order.status === 'completed' || order.status === 'canceled' || order.status === 'out_for_delivery';
      const targetList = isCompleted ? completedItems : pendingItems;

      order.cart.forEach(cartItem => {
        targetList[cartItem.product] = (targetList[cartItem.product] || 0) + cartItem.quantity;
      });
    });

    return {
      count: todaysOrders.length,
      // Converte os objetos em arrays ordenados para facilitar a renderização
      pendingItems: Object.entries(pendingItems).sort(([a], [b]) => a.localeCompare(b)),
      completedItems: Object.entries(completedItems).sort(([a], [b]) => a.localeCompare(b)),
    };

  }, [allOrders]);

  // Agora que todos os hooks foram chamados, podemos fazer os retornos condicionais.
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        backgroundColor: '#f4f4f9',
        fontFamily: 'Arial, sans-serif'
      }}>
        <img src={logo} alt="Bom Jesus" style={{ width: '200px', marginBottom: '30px' }} />
        <div style={{
          border: '5px solid #e0e0e0',
          borderTop: '5px solid #d32f2f',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
        <h2 style={{ marginTop: '20px', color: '#555' }}>Carregando...</h2>
      </div>
    );
  }
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <div className={styles.layout}>
      <div className={styles.mainContent}>
        <div className={`${styles.topBar} ${openChats.length > 0 ? styles.chatOpen : ''}`}>
          <StockStatus products={products} loading={loading} error={error} />
          <div className={styles.topRightControls}>
            {/* --- LINHA 1: BARRA DE PESQUISA --- */}
            <div className={styles.searchRow}>
              <div className={orderFilterStyles.searchWrapper}>
                <FaSearch className={orderFilterStyles.searchIcon} />
                <input
                  type="text"
                  placeholder="Buscar por cliente, nº ou telefone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={orderFilterStyles.searchInput}
                />
              </div>
            </div>

            {/* --- LINHA 2: FILTROS E ESTOQUE --- */}
            <div className={styles.filterRow}>
              <label className={orderFilterStyles.checkboxLabel}>
                <input type="checkbox" checked={showOnlyPix} onChange={() => setShowOnlyPix(prev => !prev)} />
                Pix
              </label>
              <label className={orderFilterStyles.checkboxLabel}>
                <input type="checkbox" checked={showWithSideDishes} onChange={() => setShowWithSideDishes(prev => !prev)} />
                Acompanhamentos
              </label>
              <DateFilter selectedDate={selectedDate} onDateChange={setSelectedDate} onClearFilter={() => setSelectedDate('')} />
              {user?.role === 'admin' && (
                <button className={styles.editStockBtn} onClick={() => setIsEditStockModalOpen(true)}>
                  Editar Estoque
                </button>
              )}
            </div>

            {/* --- LINHA 3: BOTÕES DE AÇÃO --- */}
            <div className={styles.actionRow}>
              {user?.role === 'admin' && (
                <>
                  <button className={styles.salesSummaryBtn} onClick={() => setIsSalesSummaryModalOpen(true)}>Vendas</button>
                  <button className={styles.chatToggleBtn} onClick={() => setIsChatMenuExpanded(prev => !prev)}>
                    Chats {unreadCount > 0 && <span className={styles.chatBadge}>{unreadCount}</span>}
                  </button>
                  <button className={styles.manageMotoboysBtn} onClick={() => setIsMotoboyModalOpen(true)}>Motoboys</button>
                </>
              )}
              <button className={styles.registerSaleBtn} onClick={() => { setSaleModalMode('sale'); setSaleModalInitialData(null); setIsSaleModalOpen(true); }}>VENDER</button>
            </div>
          </div>
        </div>

        <div className={`${styles.columnsContainer} ${mobileView !== 'pedidos' ? styles.hiddenOnMobile : ''}`}>
          {columnsToRender.map(columnName => {
            const itemsInColumn = columns[columnName];
            const isCustomerColumn = columnName === 'Em Atendimento';

            return (
            <StatusColumn
              key={columnName}
              title={columnName}
            >{isCustomerColumn
                ? itemsInColumn.map(customer => {
                    const hasNewMessage = notifications[customer.id] === true;

                    return <CustomerCard
                      key={customer.id}
                      customer={customer}
                      onSelectCustomer={handleOpenChat} // Renomeado para refletir a ação
                      hasNewMessage={hasNewMessage}
                    />;
                  })
                : itemsInColumn.map(order => (
                    <OrderCard
                      key={order.id}
                      order={order}
                      onOpenChat={() => {
                        // Cria um objeto "cliente" simples a partir dos dados do pedido
                        // para passar para a função que abre o chat.
                        const customerFromOrder = {
                          id: order.customerId,
                          name: order.customerName,
                        };
                        handleOpenChat(customerFromOrder);
                      }}
                      onUpdateStatus={handleUpdateOrderStatus}
                      onConfirmPayment={handleConfirmPayment}
                      onEditOrder={handleEditOrder}
                    />
                  ))
              }
            </StatusColumn>
            );
          })}
        </div>
      </div>

      {/* Renderiza o conteúdo de "Vender" em tela cheia no mobile */}
      <div className={`${styles.mobileFullscreenView} ${mobileView === 'vender' ? styles.visible : ''}`}>
        <RegisterSaleModal isOpen={mobileView === 'vender'} onClose={() => setMobileView('pedidos')} products={products} isMobileView={true} />
      </div>

      {/* Renderiza o conteúdo de "Estoque" em tela cheia no mobile */}
      <div className={`${styles.mobileFullscreenView} ${mobileView === 'estoque' ? styles.visible : ''}`}>
        <div className={styles.mobileContent}>
          <button onClick={() => setMobileView('pedidos')} className={mobileStyles.mobileCloseBtn}>&larr; Voltar para Pedidos</button>
          <h2>Estoque Atual</h2>
          <StockStatus products={products} loading={loading} error={error} />
        </div>
      </div>
      {/* --- NOVA ÁREA DE CHAT FLUTUANTE --- */}
      <div className={styles.chatContainer}>
        {/* A janela de chat ativa */}
        {activeCustomer && (
            <ChatView
                key={activeCustomer.id}
                customer={activeCustomer}
                order={activeOrder}
                messages={chatMessages}
                isLoading={isChatLoading}
                onClose={() => handleCloseChat(activeCustomer.id)}
                onAdminMessageSent={handleAdminMessageSent}
                onConfirmPayment={handleConfirmPayment}
                onPushCart={handlePushCart} // Passa a função para o ChatView
            />
        )}
        {/* O menu de chats, que só aparece se estiver expandido */}
        {isChatMenuExpanded && (
          <ChatSidebar
            chats={openChats}
            activeChatId={activeChatId}
            customers={customers}
            lastReadTimestamps={lastReadTimestamps}
            onSelectChat={handleSelectActiveChat}
            onCloseChat={handleCloseChat}
            onCollapse={() => setIsChatMenuExpanded(false)}
          />
        )}
      </div>
      
      {/* O modal de venda para desktop continua funcionando normalmente */}
      <RegisterSaleModal
        isOpen={isSaleModalOpen}
        onClose={() => {
            setIsSaleModalOpen(false);
            setSaleModalMode('sale'); // Reseta para o padrão
            setSaleModalInitialData(null);
        }}
        products={products}
        isMobileView={false}
        mode={saleModalMode}
        initialData={saleModalInitialData}
      />

      <ManageMotoboysModal
        isOpen={isMotoboyModalOpen}
        onClose={() => setIsMotoboyModalOpen(false)}
      />
      <SalesSummaryModal
        isOpen={isSalesSummaryModalOpen}
        onClose={() => setIsSalesSummaryModalOpen(false)}
        salesData={todaysSalesData}
      />
      <EditStockModal
        isOpen={isEditStockModalOpen}
        onClose={() => setIsEditStockModalOpen(false)}
      />

      {/* Adiciona a barra de navegação móvel */}
      <MobileNav activeView={mobileView} setActiveView={setMobileView} />
    </div>
  );
}

export default Dashboard;